import { useCallback, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'

import { saveFile } from '@/shared/api/download'
import { useApiClient } from '@/shared/api/use-api-client'
import { Button } from '@/shared/ui'
import { Download } from '@/shared/ui/icons'

/**
 * "Export results", as the mockups' Course Catalog has it: downloads every row the search
 * matches, in the shown columns, as the CSV the API streams from `{path}/export`.
 */
export function ExportButton(props: {
  /**
   * The export's path and query, such as `/site-management/v1/users/export?q=...`; `null`
   * while there is nothing to export yet, which disables the button.
   */
  readonly path: string | null
  /** The file's name when the API gives none. */
  readonly fallbackName: string
}) {
  const intl = useIntl()
  const client = useApiClient()
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)
  const running = useRef<AbortController | null>(null)
  const { path, fallbackName } = props

  // Leaving the page cancels a download still running, and nothing is saved.
  useEffect(() => () => running.current?.abort(), [])

  const exportResults = useCallback(async () => {
    if (running.current !== null || path === null) {
      return
    }
    const controller = new AbortController()
    running.current = controller
    setBusy(true)
    setFailed(false)
    try {
      const file = await client.download(path, { signal: controller.signal })
      saveFile(file.blob, file.fileName ?? fallbackName)
    } catch {
      // The client has logged a failure; a cancellation is none.
      if (!controller.signal.aborted) {
        setFailed(true)
      }
    } finally {
      if (!controller.signal.aborted) {
        running.current = null
        setBusy(false)
      }
    }
  }, [client, path, fallbackName])
  const start = useCallback(() => void exportResults(), [exportResults])

  return (
    <>
      {/* Always present, so a screen reader hears the export start. */}
      <output className="sr-only">{busy ? intl.formatMessage({ id: 'search.export.busy' }) : ''}</output>
      {failed && (
        <span role="alert" className="text-sm text-error-text">
          {intl.formatMessage({ id: 'search.export.error' })}
        </span>
      )}
      <Button variant="outlined" size="small" onClick={start} busy={busy} disabled={path === null}>
        <Download size={16} />
        {intl.formatMessage({ id: busy ? 'search.export.busy' : 'search.export' })}
      </Button>
    </>
  )
}
